import {
  type DetailedOrganizationRes,
  type HeadlineResponseOrganizationResponseRelatedPagedData,
  type OrgTypeClassHierarchyResPagedData,
  type RosterCompResAllTimeCommitsRelatedResRelatedPagedData,
  type RosterItemResponseOrganizationRosterResponseRelatedPagedData,
  type ScoreboardResponseRelatedScheduleResponseRelatedPagedData,
  type SingleTeamRankingResponse,
} from '@on3/api';
import { useAuth } from '@on3/ui-lib/contexts/AuthProvider';
import { adjustColorForReadability } from '@on3/ui-lib/utils/accessibility/color';
import { handleKeyDown } from '@on3/ui-lib/utils/accessibility/keyDown';
import { databaseUrl } from '@on3/ui-lib/utils/api';
import { useDynamicRoute } from '@on3/ui-lib/utils/hooks/useDynamicRoute';
import { SchoolRankings } from '@public/components/HighSchool/Modules/SchoolRankings';
import { On3Avatar } from '@public/components/Image/On3Avatar';
import { AdSidebarLayout } from '@public/components/Layouts/AdSidebarLayout';
import { HighSchoolSchedule } from '@public/components/Schedules/HighSchool/Schedule/HighSchoolSchedule';
import { Text } from '@public/components/UI/Typography/Text';
import clsx from 'clsx';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { FormerAthletes } from './FormerAthletes';
import { HighSchoolNewsModule } from './HighSchoolNewsModule';
import styles from './HighSchoolPage.module.scss';
import { HighSchoolNav } from './Nav/HighSchoolNav';
import { RosterModule } from './Roster/RosterModule';
import { SocialHandles } from './Socials/SocialHandles';

interface IHighSchoolPageProps {
  orgInfo?: DetailedOrganizationRes;
  formerAthletes?: RosterCompResAllTimeCommitsRelatedResRelatedPagedData;
  highSchoolArticles?: HeadlineResponseOrganizationResponseRelatedPagedData;
  currentAthletes?: RosterItemResponseOrganizationRosterResponseRelatedPagedData;
  schedule?: ScoreboardResponseRelatedScheduleResponseRelatedPagedData;
  rankings: SingleTeamRankingResponse | null;
  classification: OrgTypeClassHierarchyResPagedData | null;
}

const DEFAULT_COLOR = '#1d252c';

export const INACTIVE_COLOR = '#a2a9ad';

export const HighSchoolPage = ({
  orgInfo,
  formerAthletes,
  highSchoolArticles,
  currentAthletes,
  schedule,
  rankings,
  classification,
}: IHighSchoolPageProps) => {
  const { hasRdbAccess } = useAuth();
  const { formatHref } = useDynamicRoute();
  const router = useRouter();
  const sport = (router.query.sport as string) || 'football';

  const [selectedSport, setSelectedSport] = useState(
    sport.toLowerCase() || 'football',
  );

  useEffect(() => {
    setSelectedSport(sport.toLowerCase() || 'football');
  }, [sport]);

  const [showBio, setShowBio] = useState<boolean>(false);

  const displayColor = useMemo(
    () => adjustColorForReadability(orgInfo?.primaryColor || DEFAULT_COLOR),
    [orgInfo?.primaryColor],
  );

  const backgroundColor = useCallback(
    (color?: string | null) => ({
      backgroundColor: color || DEFAULT_COLOR,
    }),
    [],
  );

  const buttonStyles = useCallback(
    (isActive: boolean) => ({
      borderColor: isActive ? displayColor : INACTIVE_COLOR,
      color: isActive ? displayColor : INACTIVE_COLOR,
    }),
    [displayColor],
  );

  return (
    <>
      <section className={styles.top}>
        <div className={styles.container}>
          <div className={styles.inner}>
            <div className={styles.graphic}>
              <div
                className={styles.graphicInner}
                style={backgroundColor(orgInfo?.primaryColor)}
              />
            </div>

            <div className={styles.hero}>
              {hasRdbAccess() && (
                <a
                  className={styles.edit}
                  href={`${databaseUrl}/organization/${orgInfo?.key}/edit/`}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Edit
                </a>
              )}

              <div className={styles.content}>
                <figure className={styles.logo}>
                  <On3Avatar
                    domain="on3static.com"
                    fit="contain"
                    height={120}
                    quality={95}
                    radiusFive
                    source={orgInfo?.defaultAsset?.source}
                    teamAvatar
                    width={120}
                  />
                </figure>

                <div className={styles.vitals}>
                  <h1>{orgInfo?.name}</h1>
                  <Text className={styles.mascotName}>{orgInfo?.mascot}</Text>
                  <Text>{orgInfo?.city}</Text>
                  {orgInfo?.primaryColor && (
                    <div className={styles.colorWrapper}>
                      <Text className={styles.colorText}>Colors:</Text>
                      <Text
                        className={styles.schoolColors}
                        style={backgroundColor(orgInfo?.primaryColor)}
                        title={orgInfo?.primaryColor}
                      />
                      <Text
                        className={styles.schoolColors}
                        style={backgroundColor(orgInfo?.secondaryColor)}
                        title={orgInfo?.secondaryColor || ''}
                      />
                    </div>
                  )}
                </div>
              </div>

              <nav className={styles.nav}>
                <Link
                  className={styles.btnSport}
                  href={formatHref({
                    options: {
                      resetQuery: true,
                    },
                  })}
                  style={buttonStyles(selectedSport === 'football')}
                >
                  Football
                </Link>

                <Link
                  className={styles.btnSport}
                  href={formatHref({
                    query: {
                      sport: 'basketball',
                    },
                  })}
                  style={buttonStyles(selectedSport === 'basketball')}
                >
                  Boys Basketball
                </Link>

                <Link
                  className={styles.btnSport}
                  href={formatHref({
                    query: {
                      sport: 'womens-basketball',
                    },
                  })}
                  style={buttonStyles(selectedSport === 'womens-basketball')}
                >
                  Girls Basketball
                </Link>
              </nav>
            </div>
          </div>
        </div>
      </section>

      <div className={styles.layout}>
        <AdSidebarLayout>
          <section className={styles.pageNav}>
            <nav className={styles.nav}>
              <div>
                {rankings && <SchoolRankings rankings={rankings} />}
                <div className={styles.links}>
                  <HighSchoolNav primaryColor={displayColor} type="grid" />
                </div>
              </div>
            </nav>

            {(classification?.list ?? []).length > 0 && (
              <ul className={styles.hierarchies}>
                {classification?.list?.map((cl) => (
                  <li key={cl.key}>
                    <span>{cl.classificationType}:</span>
                    <span>{cl.displayName}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {!!schedule?.list?.length && (
            <HighSchoolSchedule
              relatedModel={schedule.relatedModel}
              schedule={schedule.list}
            />
          )}

          <section className={styles.orgMeta}>
            {orgInfo?.bio && (
              <div>
                <h2>Bio</h2>
                {orgInfo?.bio?.length > 400 ? (
                  <div
                    aria-expanded={showBio}
                    aria-label="Organization Bio"
                    className={clsx(styles.bio, {
                      [styles.clamp]: !showBio,
                    })}
                    onClick={() => setShowBio(!showBio)}
                    onKeyDown={handleKeyDown}
                    role="button"
                    style={{ color: displayColor }}
                    tabIndex={0}
                  >
                    <p>{orgInfo?.bio}</p>
                  </div>
                ) : (
                  <p className={styles.bio}>{orgInfo?.bio}</p>
                )}
              </div>
            )}
            <SocialHandles
              primaryColor={displayColor}
              selectedSport={selectedSport}
              socials={orgInfo?.socials}
              website={orgInfo?.website}
            />
          </section>

          {!!currentAthletes?.list?.length && (
            <RosterModule currentAthletes={currentAthletes} />
          )}

          {!!formerAthletes?.list?.length && (
            <FormerAthletes formerAthletes={formerAthletes} />
          )}

          {!!highSchoolArticles?.list?.length && (
            <HighSchoolNewsModule highSchoolArticles={highSchoolArticles} />
          )}
        </AdSidebarLayout>
      </div>
    </>
  );
};
